import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { Box, FormControl, FormControlLabel, FormGroup, FormLabel } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import MDInput from 'components/MDInput';

export function InputNodeForm({ id, data, onUpdateData }) {
  const { formatMessage: msg } = useIntl();

  const { control, watch } = useForm({
    defaultValues: {
      allowedFileTypesGroup: data?.allowedFileTypesGroup || [],
      allowedFileTypes: Array.isArray(data?.allowedFileTypes)
        ? data.allowedFileTypes.join(' ')
        : data?.allowedFileTypes || '',
      maxFilesCount: data?.maxFilesCount ?? 2,
      maxFilesSize: data?.maxFilesSize ?? 2048
    }
  });

  const FILE_GROUPS = {
    PDF: ['pdf'],
    OFFICES: ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'],
    IMAGES: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg'],
    AUDIOS: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'],
    TEXTES: ['txt', 'md', 'rtf', 'csv', 'json']
  };

  const formValues = watch();

  useEffect(() => {
    onUpdateData?.({
      id,
      data: {
        ...data,
        allowedFileTypesGroup: formValues.allowedFileTypesGroup,
        allowedFileTypes: formValues.allowedFileTypes
          ? formValues.allowedFileTypes
              .split(/\s+/) // séparation par un ou plusieurs espaces
              .map((ext) => ext.trim())
              .filter(Boolean)
          : [],
        maxFilesCount: Number(formValues.maxFilesCount),
        maxFilesSize: Number(formValues.maxFilesSize)
      }
    });
  }, [formValues, data, id, onUpdateData]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Controller
        name="maxFilesCount"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            type="number"
            label={msg({ id: 'field.input.maxFilesCount.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        name="maxFilesSize"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            type="number"
            label={msg({ id: 'field.input.maxFilesSize.label' })}
            variant="filled"
            fullWidth
            size="small"
          />
        )}
      />

      <Controller
        name="allowedFileTypesGroup"
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
              {msg({ id: 'field.input.allowedFileTypesGroup.label' })}
            </FormLabel>
            <FormGroup row>
              {Object.entries(FILE_GROUPS).map(([group, extensions]) => (
                <Tooltip
                  key={msg({ id: 'field.input.allowedFileTypesGroup.' + group + '.label' })}
                  title={extensions.join(', ')}
                  placement="right"
                  arrow
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value?.includes(group) || false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            field.onChange([...(field.value || []), group]);
                          } else {
                            field.onChange(field.value.filter((val) => val !== group));
                          }
                        }}
                      />
                    }
                    label={msg({ id: 'field.input.allowedFileTypesGroup.' + group + '.label' })}
                  />
                </Tooltip>
              ))}
            </FormGroup>
          </FormControl>
        )}
      />
      <Controller
        name="allowedFileTypes"
        control={control}
        render={({ field }) => (
          <MDInput
            {...field}
            label={msg({ id: 'field.input.allowedFileTypes.edit.label' })}
            variant="filled"
            fullWidth
            size="small"
            placeholder={msg({ id: 'field.input.allowedFileTypes.label' })}
          />
        )}
      />
    </Box>
  );
}

InputNodeForm.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    allowedFileTypesGroup: PropTypes.array,
    allowedFileTypes: PropTypes.array,
    maxFilesCount: PropTypes.number,
    maxFilesSize: PropTypes.number
  }),
  onUpdateData: PropTypes.func.isRequired
};
